/**
 * screens/OutgoingScreen.tsx — the Outbound intent ledger.
 *
 * Faithful recreation of the design prototype's Outgoing surface:
 *   - intro + Timeline/List view toggle + "New call"
 *   - live counters strip (Scheduled/In-flight/Completed/Failed/Suppressed)
 *   - status + reason filter dropdowns + name/phone search
 *   - TIMELINE (default): hour axis 06:00–23:00 org-local, shaded quiet-hours
 *     band, live "now" line, lane-packed intent chips, legend
 *   - LIST: dense triage table
 *   - read-only detail drawer (schedule + plain-language basis, lineage chain,
 *     append-only attempts, activity timeline)
 *   - New-call modal (reason pills, linked booking/invoice, engine Preview,
 *     suppression warning) — operator proposes a reason, never a time.
 *
 * Wired to sk-orchestrator /v1/outbound/intents (+ /:id, + /preview).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable, ScrollView, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody, Modal, OVERLAY_POSITION } from '../components/voiceUi';
import {
  listOutboundIntents,
  getOutboundIntent,
  listOutboundReasons,
  previewOutboundIntent,
  createOutboundIntent,
  searchOutboundContacts,
  createOutboundContact,
  type OutboundIntentRow,
  type OutboundContact,
} from '../services/voice/voiceService';
import { toE164, isValidMobile, formatPhone, phoneDigits, looksLikePhone } from '../utils/phone';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

// Status dot styles — authoritative from the design handoff (semantic hues:
// blue=live/scheduled, green=completed, amber=failed, red=hard-block, grey=receded).
// kind: 'ring' = hollow outline, 'pulse' = live dialing, 'solid' = filled.
type StatusKind = 'ring' | 'pulse' | 'solid';
type StatusMeta = { label: string; color: string; kind: StatusKind; recede?: boolean; struck?: boolean };
const GREY = '#a6a6ac';
const BLUE = '#3D6DFF';
const STATUS: Record<string, StatusMeta> = {
  scheduled: { label: 'Scheduled', color: BLUE, kind: 'ring' },
  dispatching: { label: 'Dialing now', color: BLUE, kind: 'pulse' },
  completed: { label: 'Completed', color: '#3FA46A', kind: 'solid' },
  failed_terminal: { label: 'Failed', color: '#C98A2B', kind: 'solid' },
  suppressed: { label: 'Suppressed', color: '#D65A4E', kind: 'ring' },
  superseded: { label: 'Superseded', color: GREY, kind: 'solid', recede: true, struck: true },
  cancelled: { label: 'Cancelled', color: GREY, kind: 'solid', recede: true },
  expired: { label: 'Expired', color: GREY, kind: 'solid', recede: true },
};

// One dot renderer used everywhere (counters, timeline chips, list, legend, drawer)
// so styling stays consistent with the design's obDot().
function StatusDot({ meta, size = 8 }: { meta: StatusMeta; size?: number }) {
  const hollow = meta.kind === 'ring';
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        backgroundColor: hollow ? 'transparent' : meta.color,
        borderWidth: hollow ? 2 : 0,
        borderColor: meta.color,
      }}
    />
  );
}
const HOUR_START = 6;
const HOUR_END = 23;
const QUIET_START = 21;
const QUIET_END = 9;

const localHourFraction = (iso: string): number => { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; };
const pctOfAxis = (hf: number): number => ((Math.max(HOUR_START, Math.min(HOUR_END, hf)) - HOUR_START) / (HOUR_END - HOUR_START)) * 100;
function fmtClock(iso: string): string {
  const d = new Date(iso); const h = d.getHours(); const m = d.getMinutes();
  const ap = h < 12 ? 'AM' : 'PM'; const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}
// Relative time vs now: "in 1h 50m" / "2h 20m ago" / "now" (matches design obRel).
function relTime(iso: string): string {
  const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (Math.abs(diffMin) < 1) return 'now';
  const fmt = (mins: number) => { const h = Math.floor(mins / 60); const m = mins % 60; return `${h ? `${h}h ` : ''}${m}m`; };
  return diffMin > 0 ? `in ${fmt(diffMin)}` : `${fmt(-diffMin)} ago`;
}

export default function OutgoingScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role, orgId } = useConsole();
  const canCreate = role !== 'viewer';

  const [rows, setRows] = useState<OutboundIntentRow[]>([]);
  const [reasons, setReasons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'timeline' | 'list'>('timeline');
  const [fStatus, setFStatus] = useState('all');
  const [fReason, setFReason] = useState('all');
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [openNew, setOpenNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Window to TODAY (org-local) — the timeline is a today view; without this it
    // loaded previous days' intents onto today's axis. midnight → end-of-day.
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(); dayEnd.setHours(23, 59, 59, 999);
    const [i, r] = await Promise.all([
      listOutboundIntents({ from: dayStart.toISOString(), to: dayEnd.toISOString() }),
      listOutboundReasons(),
    ]);
    setRows(i.data); setReasons(r.data); setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, orgId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((r) =>
      (fStatus === 'all' || r.status === fStatus) &&
      (fReason === 'all' || r.reasonCode === fReason) &&
      (qq === '' || [r.customerName, r.customerPhone, r.customerId].some((v: string | null | undefined) => v?.toLowerCase().includes(qq))));
  }, [rows, fStatus, fReason, q]);

  const counters = useMemo(() => {
    const n = (s: string) => rows.filter((x) => x.status === s).length;
    return [
      { label: 'Scheduled today', val: n('scheduled'), meta: STATUS.scheduled },
      { label: 'In-flight', val: n('dispatching'), meta: STATUS.dispatching },
      { label: 'Completed', val: n('completed'), meta: STATUS.completed },
      { label: 'Failed', val: n('failed_terminal'), meta: STATUS.failed_terminal },
      { label: 'Suppressed', val: n('suppressed'), meta: STATUS.suppressed },
    ];
  }, [rows]);

  const openDetail = async (id: string) => { setSelId(id); setDetail(null); setDetail(await getOutboundIntent(id)); };

  return (
    <ScreenBody maxWidth={1300}>
      {/* Header row: intro text left, controls pinned to the right end (top-aligned
          so a wrapping intro doesn't drag the toggle/button around). */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <Text style={{ maxWidth: 660, fontSize: 12.5, color: c.ink3, lineHeight: 19 }}>
          A view over the intent ledger. The deterministic engine owns <Text style={{ fontStyle: 'italic' }}>when</Text> SK Voice calls — you watch what it decided, and why. No drag-to-reschedule; timing recomputes itself when a source changes.
        </Text>
        {/* spacer pushes the toggle + New-call to the right padding edge */}
        <View style={{ flex: 1 }} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3 ?? c.paper2, borderRadius: 9 }}>
            {(['timeline', 'list'] as const).map((v) => (
              <Pressable key={v} onPress={() => setView(v)}
                style={{ height: 30, paddingHorizontal: 18, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: view === v ? c.card : 'transparent' }}>
                <Text style={{ fontSize: 12.5, fontWeight: view === v ? '600' : '500', color: view === v ? c.ink : c.ink3 }}>{v === 'timeline' ? 'Timeline' : 'List'}</Text>
              </Pressable>
            ))}
          </View>
          {canCreate ? <Button kind="primary" label="+ New call" onPress={() => setOpenNew(true)} /> : <Chip kind="mono">read-only</Chip>}
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {counters.map((ct) => (
          <View key={ct.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 9, height: 42, paddingHorizontal: 15, borderWidth: 1, borderColor: c.line, borderRadius: 10, backgroundColor: c.card }}>
            <StatusDot meta={ct.meta} size={8} />
            <Text style={{ fontFamily: theme.font.mono, fontSize: 17, fontWeight: '600', color: c.ink }}>{ct.val}</Text>
            <Text style={{ fontSize: 12, color: c.ink3 }}>{ct.label}</Text>
          </View>
        ))}
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap', zIndex: 20 } as any}>
        <Dropdown value={fStatus} onChange={setFStatus} options={[['all', 'All statuses'], ...Object.keys(STATUS).map((s) => [s, STATUS[s].label] as [string, string])]} />
        <Dropdown value={fReason} onChange={setFReason} options={[['all', 'All reasons'], ...reasons.map((r) => [r.reasonCode, r.reasonCode] as [string, string])]} />
        <View style={{ flex: 1 }} />
        <TextInput value={q} onChangeText={setQ} placeholder="Search name or phone" placeholderTextColor={c.ink4}
          style={{ width: 240, height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, color: c.ink, backgroundColor: c.card, fontSize: 12.5 }} />
      </View>

      {loading ? <ActivityIndicator color={c.ink3} />
        : filtered.length === 0 ? (
          <View style={{ padding: 56, alignItems: 'center', borderWidth: 1, borderColor: c.lineStrong, borderRadius: 12, backgroundColor: c.paper2, borderStyle: 'dashed' }}>
            <Text style={{ fontWeight: '600', fontSize: 14, color: c.ink }}>No matching intents</Text>
            <Text style={{ fontSize: 12.5, color: c.ink3, maxWidth: 440, marginTop: 6, textAlign: 'center', lineHeight: 19 }}>
              Intents arrive automatically from bookings, invoices and other products via signed webhooks — and you can create one manually with New call.
            </Text>
          </View>
        ) : view === 'timeline' ? <TimelineView rows={filtered} onOpen={openDetail} />
          : <ListView rows={filtered} onOpen={openDetail} />}

      <DetailDrawer id={selId} detail={detail} onClose={() => { setSelId(null); setDetail(null); }} />
      <NewCallModal open={openNew} reasons={reasons} onClose={() => setOpenNew(false)} onCreated={() => { setOpenNew(false); void load(); }} />
    </ScreenBody>
  );
}

function TimelineView({ rows, onOpen }: { rows: OutboundIntentRow[]; onOpen: (id: string) => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  const hours: number[] = [];
  for (let h = HOUR_START; h <= HOUR_END; h += 3) hours.push(h);
  const nowFrac = new Date().getHours() + new Date().getMinutes() / 60;
  const nowPct = pctOfAxis(nowFrac);

  const CHIP_W = 13;
  const sorted = [...rows].sort((a, b) => new Date(a.callAt).getTime() - new Date(b.callAt).getTime());
  const laneEnds: number[] = [];
  const placed = sorted.map((r) => {
    const left = pctOfAxis(localHourFraction(r.callAt));
    let lane = laneEnds.findIndex((end) => left >= end);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
    laneEnds[lane] = left + CHIP_W + 1;
    return { r, left, lane };
  });
  const bandHeight = Math.max(1, laneEnds.length) * 46 + 14;
  const bandSegs = [
    { from: Math.max(HOUR_START, QUIET_START), to: HOUR_END },
    { from: HOUR_START, to: Math.min(HOUR_END, QUIET_END) },
  ].filter((s) => s.to > s.from);

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View style={{ minWidth: 1180 }}>
          <View style={{ height: 24, borderBottomWidth: 1, borderBottomColor: c.line }}>
            {hours.map((h) => (
              <Text key={h} style={{ position: 'absolute', left: `${pctOfAxis(h)}%`, top: 4, fontFamily: theme.font.mono, fontSize: 10, color: c.ink4, transform: [{ translateX: -12 }] } as any}>
                {String(h).padStart(2, '0')}:00
              </Text>
            ))}
          </View>
          <View style={{ height: bandHeight, paddingTop: 10 }}>
            {bandSegs.map((s, i) => (
              <View key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${pctOfAxis(s.from)}%`, width: `${pctOfAxis(s.to) - pctOfAxis(s.from)}%`, backgroundColor: c.paper2, opacity: 0.7, borderLeftWidth: 1, borderRightWidth: 1, borderColor: c.lineStrong, borderStyle: 'dashed' } as any} />
            ))}
            {nowFrac >= HOUR_START && nowFrac <= HOUR_END && (
              <>
                <View style={{ position: 'absolute', top: 0, bottom: 0, left: `${nowPct}%`, width: 2, backgroundColor: '#2D5CF6', zIndex: 5 } as any} />
                <View style={{ position: 'absolute', top: 0, left: `${nowPct}%`, transform: [{ translateX: -14 }], backgroundColor: '#2D5CF6', borderBottomLeftRadius: 5, borderBottomRightRadius: 5, zIndex: 6, paddingHorizontal: 6, paddingVertical: 1 } as any}>
                  <Text style={{ color: '#fff', fontSize: 9, fontFamily: theme.font.mono }}>now</Text>
                </View>
              </>
            )}
            {placed.map(({ r, left, lane }) => {
              const meta = STATUS[r.status] ?? STATUS.scheduled;
              return (
                <Pressable key={r.id} onPress={() => onOpen(r.id)}
                  style={{ position: 'absolute', left: `${left}%`, top: 10 + lane * 46, width: 150, flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 8, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: c.line, backgroundColor: c.card, opacity: meta.recede ? 0.55 : 1 } as any}>
                  <StatusDot meta={meta} size={7} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text numberOfLines={1} style={{ fontWeight: '600', fontSize: 12, color: c.ink, textDecorationLine: meta.struck ? 'line-through' : 'none' }}>{r.customerDisplay ?? r.customerPhone ?? r.customerId}</Text>
                    <Text numberOfLines={1} style={{ fontFamily: theme.font.mono, fontSize: 9.5, color: c.ink4 }}>{r.reasonCode}</Text>
                  </View>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 9.5, color: c.ink3 }}>{fmtClock(r.callAt)}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      </ScrollView>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, padding: 12, borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2, flexWrap: 'wrap' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <View style={{ width: 20, height: 12, borderRadius: 3, backgroundColor: c.paper2, borderWidth: 1, borderColor: c.lineStrong }} />
          <Text style={{ fontSize: 11, color: c.ink3 }}>quiet hours — engine never schedules here</Text>
        </View>
        {[['scheduled', 'Scheduled'], ['dispatching', 'Dialing'], ['completed', 'Completed'], ['failed_terminal', 'Failed'], ['suppressed', 'Suppressed'], ['superseded', 'Superseded / expired']].map(([k, label]) => (
          <View key={k} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <StatusDot meta={STATUS[k as string]} size={8} />
            <Text style={{ fontSize: 11, color: c.ink3 }}>{label}</Text>
          </View>
        ))}
      </View>
    </Card>
  );
}

function ListView({ rows, onOpen }: { rows: OutboundIntentRow[]; onOpen: (id: string) => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  // Columns: last one (Latest) FLEXES to fill, so there's no trailing gap.
  const cols = [
    { h: 'Status', w: 128 },
    { h: 'Customer', w: 200 },
    { h: 'Reason', w: 230 },
    { h: 'Scheduled for', w: 150 },
    { h: 'Basis', w: 150 },
    { h: 'Attempts', w: 90 },
    { h: 'Latest', flex: 1 as const },
  ];
  const colStyle = (i: number) => (cols[i].flex ? { flex: 1 } : { width: cols[i].w });
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View style={{ minWidth: 980, flexGrow: 1 } as any}>
          <View style={{ flexDirection: 'row', gap: 12, paddingHorizontal: 16, paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper2 }}>
            {cols.map((col, i) => <Text key={col.h} style={{ ...colStyle(i), fontSize: 10, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4 }}>{col.h}</Text>)}
          </View>
          {rows.map((r, ri) => {
            const meta = STATUS[r.status] ?? STATUS.scheduled;
            // Name if the CRM resolved one; else the phone; never the raw ctc_ id.
            const name = r.customerName ?? null;
            const phone = r.customerPhone ?? null;
            const latest = latestLabel(r);
            return (
              <Pressable key={r.id} onPress={() => onOpen(r.id)}
                style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: ri === rows.length - 1 ? 0 : 1, borderBottomColor: c.line, opacity: meta.recede ? 0.6 : 1 }}>
                <View style={{ ...colStyle(0), flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <StatusDot meta={meta} size={7} />
                  <Text style={{ fontSize: 12, color: c.ink2, textDecorationLine: meta.struck ? 'line-through' : 'none' }}>{meta.label}</Text>
                </View>
                {/* Customer: name on top, phone beneath (design). Falls back to phone-as-title when no name. */}
                <View style={colStyle(1)}>
                  <Text style={{ fontWeight: '600', fontSize: 12.5, color: c.ink }} numberOfLines={1}>{name ?? phone ?? r.customerId}</Text>
                  {name && phone ? <Text style={{ fontFamily: theme.font.mono, fontSize: 11, color: c.ink4, marginTop: 1 }} numberOfLines={1}>{phone}</Text> : null}
                </View>
                <View style={{ ...colStyle(2), flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink2 }} numberOfLines={1}>{r.reasonCode}</Text>
                  <Chip kind={r.regulatoryClass === 'promotional' ? 'mono' : 'default'}>{r.regulatoryClass === 'promotional' ? 'promotional' : 'transactional'}</Chip>
                </View>
                <View style={colStyle(3)}>
                  <Text style={{ fontSize: 12.5, color: c.ink2 }} numberOfLines={1}>{fmtClock(r.callAt)}</Text>
                  <Text style={{ fontSize: 11, color: c.ink4, marginTop: 1 }} numberOfLines={1}>{relTime(r.callAt)}</Text>
                </View>
                <Text style={{ ...colStyle(4), fontSize: 11.5, color: c.ink2 }} numberOfLines={1}>{basisLabel(r.schedulingBasis)}</Text>
                <Text style={{ ...colStyle(5), fontFamily: theme.font.mono, fontSize: 12, color: c.ink2 }}>{r.attemptNo}{r.maxAttempts ? ` / ${r.maxAttempts}` : ''}</Text>
                <Text style={{ ...colStyle(6), fontSize: 11.5, color: latest.dim ? c.ink4 : c.ink2 }} numberOfLines={1}>{latest.text}</Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </Card>
  );
}

// "Latest" column: the most recent attempt's real outcome, else the status.
function latestLabel(r: OutboundIntentRow): { text: string; dim: boolean } {
  const o = r.latestOutcome;
  if (o) {
    const map: Record<string, string> = { connected: 'Connected', no_answer: 'No answer', busy: 'Busy', voicemail: 'Voicemail', declined_mid_call: 'Declined', stop_request: 'Opted out', network_failure: 'Network error' };
    return { text: map[o] ?? o, dim: false };
  }
  const meta = STATUS[r.status] ?? STATUS.scheduled;
  return { text: meta.recede ? '—' : meta.label, dim: true };
}

// Humanize the scheduling basis for display.
function basisLabel(basis?: string | null): string {
  if (!basis) return '—';
  return basis.replace(/_/g, ' ');
}

function DetailDrawer({ id, detail, onClose }: { id: string | null; detail: any; onClose: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  if (!id) return null;
  const intent = detail?.intent;
  const events: any[] = detail?.events ?? [];
  const attempts: any[] = detail?.attempts ?? [];
  const meta = intent ? (STATUS[intent.status] ?? STATUS.scheduled) : STATUS.scheduled;
  const clsLabel = intent?.regulatoryClass === 'promotional' ? 'promotional' : 'transactional';
  const name = intent?.customerName ?? null;
  const phone = intent?.customerPhone ?? null;
  const title = name ?? phone ?? intent?.customerId ?? '';
  const ctx = intent?.contextPayload ?? {};
  const note = typeof ctx?.note === 'string' ? ctx.note : null;
  return (
    <>
      <Pressable onPress={onClose} style={{ position: OVERLAY_POSITION, top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.25)', zIndex: 49 } as any} />
      <View style={{ position: OVERLAY_POSITION, top: 0, bottom: 0, right: 0, width: 480, maxWidth: '100%', backgroundColor: c.card, borderLeftWidth: 1, borderLeftColor: c.line, zIndex: 50 } as any}>
        {!detail ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}><ActivityIndicator color={c.ink3} /></View>
        ) : (
          <View style={{ flex: 1 }}>
            {/* Header — customer + class chip, reason + phone, status line */}
            <View style={{ padding: 18, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 16, fontWeight: '700', color: c.ink }}>{title}</Text>
                    <Chip kind={intent?.regulatoryClass === 'promotional' ? 'mono' : 'default'}>{clsLabel}</Chip>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3 }}>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink3 }}>{intent?.reasonCode}</Text>
                    {phone ? <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink4 }}>{phone}</Text> : null}
                  </View>
                </View>
                <Pressable onPress={onClose} hitSlop={8}><Text style={{ fontSize: 20, color: c.ink3 }}>×</Text></Pressable>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 10 }}>
                <StatusDot meta={meta} size={9} />
                <Text style={{ fontSize: 13, fontWeight: '600', color: meta.recede ? c.ink4 : c.ink }}>{meta.label}</Text>
                <Text style={{ fontSize: 12, color: c.ink4 }}>· {intent?.origin ?? 'system'} · {intent ? relTime(intent.callAt) : ''}</Text>
              </View>
            </View>

            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
              {/* Suppress note (protection working) */}
              {intent?.status === 'suppressed' && (
                <View style={{ flexDirection: 'row', gap: 10, padding: 12, borderRadius: 10, backgroundColor: `${STATUS.suppressed.color}12`, borderWidth: 1, borderColor: `${STATUS.suppressed.color}44`, marginBottom: 16 }}>
                  <Text style={{ fontSize: 12, color: c.ink2, flex: 1, lineHeight: 18 }}>
                    Nothing was dialed. This intent was <Text style={{ fontWeight: '700' }}>deliberately blocked</Text> to protect the customer — consent for a promotional call was missing. Protection working, not a failure.
                  </Text>
                </View>
              )}

              {/* Schedule */}
              <Section title="Schedule">
                <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden' }}>
                  <KV label="Call at" value={intent ? `${fmtClock(intent.callAt)} · ${dayLabel(intent.callAt)}` : '—'} first />
                  <KV label="Expires" value={intent ? `${fmtClock(intent.expiresAt)} · ${dayLabel(intent.expiresAt)}` : '—'} />
                  <View style={{ padding: 12, backgroundColor: c.paper2 }}>
                    <Text style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4, marginBottom: 4 }}>
                      Why this time · {basisLabel(intent?.schedulingBasis)}
                    </Text>
                    <Text style={{ fontSize: 12.5, color: c.ink2, lineHeight: 18 }}>{whyThisTime(intent)}</Text>
                  </View>
                </View>
              </Section>

              {note ? (
                <Section title="Context note"><Text style={{ fontSize: 12.5, color: c.ink2, lineHeight: 18 }}>{note}</Text></Section>
              ) : null}

              {/* Lineage — only when there's a chain */}
              {(intent?.supersedesIntentId || intent?.parentIntentId || (intent?.rootIntentId && intent?.rootIntentId !== intent?.id)) ? (
                <Section title="Lineage · one source, several intents">
                  <KVbox>
                    <KV label="Root" value={intent?.rootIntentId ?? '—'} mono first />
                    {intent?.supersedesIntentId ? <KV label="Supersedes" value={intent.supersedesIntentId} mono /> : null}
                    {intent?.parentIntentId ? <KV label="Retry of" value={intent.parentIntentId} mono /> : null}
                  </KVbox>
                </Section>
              ) : null}

              {/* Attempt history · append-only */}
              <Section title="Attempt history · append-only">
                {attempts.length === 0 ? (
                  <Text style={{ fontSize: 12, color: c.ink4 }}>No attempts yet.</Text>
                ) : (
                  <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden' }}>
                    {attempts.map((a, ai) => (
                      <View key={a.id} style={{ padding: 12, borderTopWidth: ai === 0 ? 0 : 1, borderTopColor: c.line }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink3 }}>#{ai + 1}</Text>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink }}>{a.dialedAt ? fmtClock(a.dialedAt) : '—'}</Text>
                          <View style={{ flex: 1 }} />
                          <Text style={{ fontSize: 12, fontWeight: '600', color: outcomeColor(a.outcome, c) }}>{outcomeText(a.outcome)}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 5 }}>
                          <Text style={{ fontSize: 11, color: c.ink4 }}>consent · <Text style={{ color: c.ink2 }}>{a.consentResult ?? '—'}</Text></Text>
                          <Text style={{ fontSize: 11, color: c.ink4 }}>counted · {a.countedVsCap ? 'yes' : 'no'}</Text>
                          {a.providerCallRef ? <Text style={{ fontSize: 11, color: c.ink4 }}>ref · <Text style={{ fontFamily: theme.font.mono, color: c.ink2 }}>{a.providerCallRef}</Text></Text> : null}
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </Section>

              {/* Activity · status transitions */}
              <Section title="Activity · status transitions">
                {events.length === 0 ? <Text style={{ fontSize: 12, color: c.ink4 }}>No transitions recorded.</Text> : (
                  <View style={{ gap: 10 }}>
                    {events.map((e) => {
                      const em = STATUS[e.toStatus] ?? STATUS.scheduled;
                      return (
                        <View key={e.id} style={{ flexDirection: 'row', gap: 10 }}>
                          <View style={{ paddingTop: 3 }}><StatusDot meta={em} size={8} /></View>
                          <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <Text style={{ fontSize: 12.5, fontWeight: '600', color: c.ink2 }}>{em.label}</Text>
                              <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.mono }}>{e.occurredAt ? fmtClock(e.occurredAt) : ''}</Text>
                            </View>
                            {e.reason ? <Text style={{ fontSize: 11.5, color: c.ink4, marginTop: 1 }}>{e.reason}</Text> : null}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </Section>
            </ScrollView>

            {/* Read-only footer (design) */}
            <View style={{ padding: 14, borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2 }}>
              <Text style={{ fontSize: 11, color: c.ink4, lineHeight: 16 }}>
                Read-only. The engine owns timing — even manual intents are immutable once created. A source change supersedes; it never edits in place.
              </Text>
            </View>
          </View>
        )}
      </View>
    </>
  );
}

// Day label for the drawer's schedule rows ("today" / "tomorrow" / date).
function dayLabel(iso: string): string {
  const d = new Date(iso); const now = new Date();
  const days = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
// Plain-language "why this time" from the intent's basis (real basisText if present).
function whyThisTime(intent: any): string {
  if (!intent) return '—';
  if (typeof intent.basisText === 'string' && intent.basisText) return intent.basisText;
  const b = basisLabel(intent.schedulingBasis);
  return `Scheduled via ${b}. SK Voice chose this time deterministically — operators propose a reason, never a time.`;
}
function outcomeText(o?: string | null): string {
  if (!o) return 'In progress';
  const map: Record<string, string> = { connected: 'Connected', no_answer: 'No answer', busy: 'Busy', voicemail: 'Voicemail', declined_mid_call: 'Declined', stop_request: 'Opted out', network_failure: 'Network error' };
  return map[o] ?? o;
}
function outcomeColor(o: string | null | undefined, c: any): string {
  if (o === 'connected') return '#3FA46A';
  if (o === 'no_answer' || o === 'busy' || o === 'network_failure') return '#C98A2B';
  if (o === 'declined_mid_call' || o === 'stop_request') return '#D65A4E';
  return c.ink3;
}
function KVbox({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden' }}>{children}</View>;
}
function KV({ label, value, mono, first }: { label: string; value: string; mono?: boolean; first?: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 11, borderTopWidth: first ? 0 : 1, borderTopColor: c.line }}>
      <Text style={{ fontSize: 12, color: c.ink3 }}>{label}</Text>
      <Text style={{ fontSize: 12.5, color: c.ink2, fontFamily: mono ? theme.font.mono : theme.font.sans, maxWidth: '65%' }} numberOfLines={1}>{value}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ marginTop: 16 }}>
      <Text style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: c.ink4, marginBottom: 8 }}>{title}</Text>
      {children}
    </View>
  );
}

// Context-note starter examples (design). Clicking one fills the note.
const NOTE_EXAMPLES = [
  'Prefers a call after 6 PM — mention the festive offer.',
  'Second reminder; was travelling last week.',
  'Wants the morning slot if one frees up.',
];
// Optional preferred-language hint → persisted on the intent, threaded to the
// call. Values are locale codes to match the engine's format (default en-IN).
const LANGUAGES: Array<[string, string]> = [
  ['', 'Auto (detect)'], ['en-IN', 'English'], ['ta-IN', 'Tamil'], ['hi-IN', 'Hindi'],
  ['te-IN', 'Telugu'], ['ml-IN', 'Malayalam'], ['kn-IN', 'Kannada'],
];
function initialsOf(name: string): string {
  const p = (name || '').trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
}

function NewCallModal({ open, reasons, onClose, onCreated }: { open: boolean; reasons: any[]; onClose: () => void; onCreated: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  // Customer picker: search CRM by name/phone → select; or create-in-place.
  // Phone is the UNIQUE key — the create path 409s a duplicate ("already exists").
  const [custQuery, setCustQuery] = useState('');
  const [results, setResults] = useState<OutboundContact[]>([]);
  const [selected, setSelected] = useState<OutboundContact | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone10, setNewPhone10] = useState('');
  const [addErr, setAddErr] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);

  const [reason, setReason] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [lang, setLang] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const selReason = reasons.find((r) => r.reasonCode === reason);
  const promo = selReason?.regulatoryClass === 'promotional';
  const newE164 = toE164(newPhone10);

  const reset = () => {
    setCustQuery(''); setResults([]); setSelected(null); setAdding(false);
    setNewName(''); setNewPhone10(''); setAddErr(null);
    setReason(''); setSourceRef(''); setLang(''); setNote(''); setPreview(null);
  };

  // Debounced search (skip while a contact is already picked).
  useEffect(() => {
    if (selected) return;
    const q = custQuery.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => { setResults(await searchOutboundContacts(q)); }, 250);
    return () => clearTimeout(t);
  }, [custQuery, selected]);

  const openAdd = () => {
    setAddErr(null);
    if (looksLikePhone(custQuery)) { setNewPhone10(phoneDigits(custQuery)); setNewName(''); }
    else { setNewName(custQuery.trim()); setNewPhone10(''); }
    setAdding(true);
  };
  const doAdd = async () => {
    if (!newName.trim() || !newE164) return;
    setAddBusy(true); setAddErr(null);
    try {
      const contact = await createOutboundContact({ name: newName.trim(), phone: newE164 });
      setSelected(contact); setAdding(false); setPreview(null);
    } catch (e: any) {
      if (e?.response?.status === 409) setAddErr('This number already exists — search for it above.');
      else handleApiError(e, 'Add customer failed');
    } finally { setAddBusy(false); }
  };

  const custPayload = () => (selected ? { customer_id: selected.id, phone: selected.phone ?? undefined } : {});
  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await previewOutboundIntent({ ...custPayload(), reason_code: reason, source_ref: sourceRef || undefined })); }
    catch (e) { handleApiError(e, 'Preview failed'); } finally { setBusy(false); }
  };
  const doCreate = async () => {
    setBusy(true);
    try {
      await createOutboundIntent({
        ...custPayload(),
        reason_code: reason,
        source_ref: sourceRef || undefined,
        preferred_language: lang || undefined,
        context_payload: note ? { note } : undefined,
      });
      showSuccess(null, 'Outbound call scheduled'); reset(); onCreated();
    } catch (e) { handleApiError(e, 'Create failed'); } finally { setBusy(false); }
  };

  const canPreview = !!selected && !!reason && !busy;

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} width={520}>
      <Text style={{ fontSize: 16, fontWeight: '700', color: c.ink, marginBottom: 3 }}>New outbound call</Text>
      <Text style={{ fontSize: 12, color: c.ink3, marginBottom: 12 }}>You propose a <Text style={{ fontStyle: 'italic' }}>reason to call</Text> — not a time. The engine computes when.</Text>
      <ScrollView style={{ maxHeight: 560 }} showsVerticalScrollIndicator={false}>
        {/* Customer */}
        <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>Customer</Text>
        {selected ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderColor: c.line, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 9 }}>
            <View style={{ width: 30, height: 30, borderRadius: 999, backgroundColor: c.paper3, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: 11, fontWeight: '600', color: c.ink2 }}>{initialsOf(selected.name)}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: c.ink }}>{selected.name || 'Unnamed'}</Text>
              <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink4 }}>{formatPhone(selected.phone ?? '')}</Text>
            </View>
            <Pressable onPress={() => { setSelected(null); setCustQuery(''); setPreview(null); }}><Text style={{ fontSize: 12, color: c.ink3 }}>Change</Text></Pressable>
          </View>
        ) : (
          <>
            <TextInput value={custQuery} onChangeText={(v) => { setCustQuery(v); setAdding(false); }} placeholder="Search by name or phone number" placeholderTextColor={c.ink4}
              style={{ height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, color: c.ink, backgroundColor: c.card, fontSize: 13 }} />
            {results.length > 0 && (
              <View style={{ marginTop: 6, borderWidth: 1, borderColor: c.line, borderRadius: 9, overflow: 'hidden', maxHeight: 190 }}>
                <ScrollView showsVerticalScrollIndicator={false}>
                  {results.map((r, i) => (
                    <Pressable key={r.id} onPress={() => { setSelected(r); setResults([]); setPreview(null); }}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 9, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: c.line }}>
                      <View style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: c.paper3, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ fontSize: 10.5, fontWeight: '600', color: c.ink2 }}>{initialsOf(r.name)}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: '600', color: c.ink }}>{r.name || 'Unnamed'}</Text>
                        <Text style={{ fontFamily: theme.font.mono, fontSize: 11, color: c.ink4 }}>{formatPhone(r.phone ?? '')}</Text>
                      </View>
                    </Pressable>
                  ))}
                </ScrollView>
              </View>
            )}
            {/* Create-in-place. Add is offered only when it isn't an existing number. */}
            {!adding && custQuery.trim().length >= 2 && (
              <Pressable onPress={openAdd} style={{ marginTop: 6, borderWidth: 1, borderStyle: 'dashed', borderColor: c.lineStrong, borderRadius: 9, paddingVertical: 10, alignItems: 'center' }}>
                <Text style={{ fontSize: 12.5, color: c.ink2 }}>+ Add “{custQuery.trim()}” to the CRM</Text>
              </Pressable>
            )}
            {adding && (
              <View style={{ marginTop: 6, borderWidth: 1, borderColor: c.line, borderRadius: 9, padding: 12, gap: 8 }}>
                <TextInput value={newName} onChangeText={setNewName} placeholder="Full name" placeholderTextColor={c.ink4}
                  style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontSize: 13 }} />
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ height: 36, paddingHorizontal: 12, justifyContent: 'center', borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.paper2 }}>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 13, color: c.ink2 }}>+91</Text>
                  </View>
                  <TextInput value={newPhone10} onChangeText={(v) => { setNewPhone10(phoneDigits(v)); setAddErr(null); }} keyboardType="number-pad" maxLength={10} placeholder="10-digit mobile" placeholderTextColor={c.ink4}
                    style={{ flex: 1, height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontFamily: theme.font.mono, fontSize: 13 }} />
                </View>
                {newPhone10.length > 0 && !isValidMobile(newPhone10) && <Text style={{ fontSize: 11, color: c.danger }}>Enter a 10-digit mobile starting 6–9.</Text>}
                {addErr && <Text style={{ fontSize: 11, color: c.danger }}>{addErr}</Text>}
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Button kind="primary" label={addBusy ? 'Adding…' : 'Add to CRM'} onPress={doAdd} disabled={!newName.trim() || !newE164 || addBusy} />
                  <Button kind="default" label="Cancel" onPress={() => { setAdding(false); setAddErr(null); }} />
                </View>
              </View>
            )}
          </>
        )}

        {/* Reason cards (code + regulatory class) */}
        <Text style={{ fontSize: 12, color: c.ink2, marginTop: 14, marginBottom: 6 }}>Reason to call</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {reasons.map((rc) => {
            const sel = reason === rc.reasonCode;
            return (
              <Pressable key={rc.reasonCode} onPress={() => { setReason(rc.reasonCode); setPreview(null); }}
                style={{ width: '31.5%', minWidth: 138, borderWidth: 1, borderColor: sel ? c.ink : c.line, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 9, backgroundColor: sel ? c.paper2 : c.card }}>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 12, fontWeight: '600', color: c.ink }}>{rc.reasonCode}</Text>
                <Text style={{ fontSize: 10.5, color: c.ink4, marginTop: 3 }}>{rc.regulatoryClass}</Text>
              </Pressable>
            );
          })}
        </View>

        {/* Linked ref + Language */}
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>Linked booking / invoice <Text style={{ color: c.ink4 }}>· optional</Text></Text>
            <MInput value={sourceRef} onChange={(v) => { setSourceRef(v); setPreview(null); }} mono />
          </View>
          <View>
            <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>Language</Text>
            <Dropdown value={lang} onChange={setLang} options={LANGUAGES} />
          </View>
        </View>
        <Text style={{ fontSize: 11, color: c.ink4, marginTop: 4 }}>The record this call is about — a booking or invoice ID from another SK product. Linking one lets SK Voice time the call around the service; leave blank for a general call.</Text>

        {/* Context note + examples */}
        <Text style={{ fontSize: 12, color: c.ink2, marginTop: 12, marginBottom: 4 }}>Context note <Text style={{ color: c.ink4 }}>· optional</Text></Text>
        <TextInput value={note} onChangeText={setNote} multiline placeholder="What should SK Voice know before the call?" placeholderTextColor={c.ink4}
          style={{ minHeight: 62, paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, color: c.ink, backgroundColor: c.card, fontSize: 13, textAlignVertical: 'top' } as any} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <Text style={{ fontSize: 11, color: c.ink4 }}>Examples:</Text>
          {NOTE_EXAMPLES.map((ex) => (
            <Pressable key={ex} onPress={() => setNote(ex)} style={{ borderWidth: 1, borderColor: c.line, borderRadius: 20, paddingHorizontal: 10, paddingVertical: 5 }}>
              <Text style={{ fontSize: 11, color: c.ink2 }}>{ex}</Text>
            </Pressable>
          ))}
        </View>

        {preview && (
          <View style={{ marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: c.paper2, borderWidth: 1, borderColor: c.line }}>
            <Text style={{ color: promo && preview.unschedulable ? c.danger : c.ink, fontSize: 13 }}>{preview.plain_language}</Text>
            {promo && <Text style={{ color: c.ink3, fontSize: 11.5, marginTop: 4 }}>Promotional — without granted consent the intent is created but suppressed (nothing dialed).</Text>}
          </View>
        )}
      </ScrollView>

      <View style={{ flexDirection: 'row', gap: 8, marginTop: 14, alignItems: 'center' }}>
        <Button kind="default" label={busy && !preview ? 'Previewing…' : 'Preview schedule'} onPress={doPreview} disabled={!canPreview} />
        {preview && <Button kind="primary" label={busy ? 'Creating…' : 'Create'} onPress={doCreate} disabled={busy} />}
      </View>
      <Text style={{ fontSize: 11, color: c.ink4, marginTop: 8, textAlign: 'center' }}>Consent and quiet hours always apply — operators can’t bypass them.</Text>
    </Modal>
  );
}

function MField({ label, children }: { label: string; children: React.ReactNode }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <View style={{ marginTop: 10 }}><Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>{label}</Text>{children}</View>;
}
function MInput({ value, onChange, mono }: { value: string; onChange: (v: string) => void; mono?: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <TextInput value={value} onChangeText={onChange} placeholderTextColor={c.ink4}
    style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontFamily: mono ? theme.font.mono : theme.font.sans, fontSize: 13 }} />;
}

function Dropdown({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  const { theme } = useTheme(); const c = theme.colors;
  const [open, setOpen] = useState(false);
  const cur = options.find(([v]) => v === value)?.[1] ?? value;
  return (
    <View style={{ position: 'relative', zIndex: open ? 1000 : 1 } as any}>
      <Pressable onPress={() => setOpen((o) => !o)}
        style={{ height: 36, paddingHorizontal: 12, minWidth: 150, flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, backgroundColor: c.card }}>
        <Text style={{ flex: 1, fontSize: 12.5, color: c.ink2 }} numberOfLines={1}>{cur}</Text>
        <Text style={{ fontSize: 10, color: c.ink4 }}>▾</Text>
      </Pressable>
      {open && (
        <>
          {/* Full-viewport backdrop so an outside click/tap closes the menu —
              without it the dropdown stayed open after clicking elsewhere. */}
          <Pressable
            onPress={() => setOpen(false)}
            style={{ position: OVERLAY_POSITION, top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 } as any}
          />
          <View style={{ position: 'absolute', top: 40, left: 0, right: 0, minWidth: 180, backgroundColor: c.card, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, zIndex: 1000, elevation: 12, overflow: 'hidden', paddingVertical: 4, shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 12, shadowOffset: { width: 0, height: 6 } } as any}>
            {options.map(([v, l]) => (
              <Pressable key={v} onPress={() => { onChange(v); setOpen(false); }} style={{ paddingHorizontal: 12, paddingVertical: 8, backgroundColor: v === value ? c.paper2 : c.card }}>
                <Text style={{ fontSize: 12.5, color: v === value ? c.ink : c.ink2, fontWeight: v === value ? '600' : '400' }} numberOfLines={1}>{l}</Text>
              </Pressable>
            ))}
          </View>
        </>
      )}
    </View>
  );
}
